import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AdminGamesService } from '@admin-panel/infra';
import { DateFormats, formatDateToString } from '@core';
import {
  DailyStatsDto,
  GetAdminGamesStatsQueryParams,
  GetAdminGamesStatsResponse,
  RegionDailyGamesStatsDto,
  RegionGameStatsDto
} from '@core-http';
import {
  AdminGamesStatsQueryResult,
  CategoryRegionBreakdownQueryResult,
  DailyStatQueryResult,
  DayRegionBreakdownQueryResult,
  DayStatsQueryResult,
  RegionDailyGameStatQueryResult,
  RegionGameStatQueryResult,
  RegionGamesGroupQueryResult,
  StatCategoryQueryResult
} from './get-admin-games-stats.query-result';

export class GetAdminGamesStatsQueryHandler {
  private readonly adminGamesService: AdminGamesService = inject(AdminGamesService);

  query(queryParams: GetAdminGamesStatsQueryParams = {}): Observable<AdminGamesStatsQueryResult> {
    return this.adminGamesService
      .getGamesStats(queryParams)
      .pipe(map((response: GetAdminGamesStatsResponse) => this.mapToResult(response)));
  }

  private mapToResult(response: GetAdminGamesStatsResponse): AdminGamesStatsQueryResult {
    const gamesMap: Map<string, number> = this.toMap(response.dailyGamesCreated);
    const appsCreatedMap: Map<string, number> = this.toMap(response.dailyApplicationsCreated);
    const appsAcceptedMap: Map<string, number> = this.toMap(response.dailyApplicationsAccepted);

    const regionGamesByDate: Map<string, Map<string, number>> = this.toDateRegionMap(
      response.regionDailyGamesCreated ?? []
    );
    const regionAppsCreatedByDate: Map<string, Map<string, number>> = this.toDateRegionMap(
      response.regionDailyApplicationsCreated ?? []
    );
    const regionAppsAcceptedByDate: Map<string, Map<string, number>> = this.toDateRegionMap(
      response.regionDailyApplicationsAccepted ?? []
    );

    const allDates: string[] = [
      ...new Set([...gamesMap.keys(), ...appsCreatedMap.keys(), ...appsAcceptedMap.keys()])
    ].sort((a: string, b: string) => b.localeCompare(a));

    return {
      byDay: allDates.map(
        (date: string): DayStatsQueryResult => ({
          date: formatDateToString(date, DateFormats.DayMonthYearDash),
          rawDate: date,
          dayOfWeek: formatDateToString(date, DateFormats.WeekdayName),
          gamesCreated: gamesMap.get(date) ?? 0,
          applicationsCreated: appsCreatedMap.get(date) ?? 0,
          applicationsAccepted: appsAcceptedMap.get(date) ?? 0,
          regions: this.buildDayRegions(date, regionGamesByDate, regionAppsCreatedByDate, regionAppsAcceptedByDate)
        })
      ),
      byCategory: [
        this.mapCategory('Utworzone gierki', response.dailyGamesCreated, regionGamesByDate),
        this.mapCategory('Aplikacje do gierek', response.dailyApplicationsCreated, regionAppsCreatedByDate),
        this.mapCategory('Zaakceptowane aplikacje', response.dailyApplicationsAccepted, regionAppsAcceptedByDate)
      ],
      byRegion: this.mapByRegion(
        response.regionDailyGamesCreated ?? [],
        response.regionDailyApplicationsCreated ?? [],
        response.regionDailyApplicationsAccepted ?? []
      ),
      regionStats: [...(response.regionStats ?? [])]
        .filter((stat: RegionGameStatsDto) => stat.gameCount > 0)
        .sort((a: RegionGameStatsDto, b: RegionGameStatsDto) => b.gameCount - a.gameCount)
        .map(
          (stat: RegionGameStatsDto): RegionGameStatQueryResult => ({
            regionName: stat.regionName,
            gameCount: stat.gameCount
          })
        )
    };
  }

  private mapCategory(
    label: string,
    stats: DailyStatsDto[],
    regionsByDate: Map<string, Map<string, number>>
  ): StatCategoryQueryResult {
    const items: DailyStatQueryResult[] = [...stats]
      .sort((a: DailyStatsDto, b: DailyStatsDto) => b.date.localeCompare(a.date))
      .map(
        (dto: DailyStatsDto): DailyStatQueryResult => ({
          date: formatDateToString(dto.date, DateFormats.DayMonthYearDash),
          rawDate: dto.date,
          dayOfWeek: formatDateToString(dto.date, DateFormats.WeekdayName),
          count: dto.count,
          regions: this.buildCategoryRegions(dto.date, regionsByDate)
        })
      );
    const total: number = items.reduce((sum: number, item: DailyStatQueryResult) => sum + item.count, 0);
    return { label, items, total };
  }

  private mapByRegion(
    gamesCreated: RegionDailyGamesStatsDto[],
    appsCreated: RegionDailyGamesStatsDto[],
    appsAccepted: RegionDailyGamesStatsDto[]
  ): RegionGamesGroupQueryResult[] {
    const appsCreatedByRegion: Map<string, Map<string, number>> = this.toRegionMap(appsCreated);
    const appsAcceptedByRegion: Map<string, Map<string, number>> = this.toRegionMap(appsAccepted);

    return [...gamesCreated]
      .map((region: RegionDailyGamesStatsDto): RegionGamesGroupQueryResult => {
        const appsCreatedMap: Map<string, number> = appsCreatedByRegion.get(region.regionName) ?? new Map();
        const appsAcceptedMap: Map<string, number> = appsAcceptedByRegion.get(region.regionName) ?? new Map();

        const dailyStats: RegionDailyGameStatQueryResult[] = [...region.dailyStats]
          .sort((a: DailyStatsDto, b: DailyStatsDto) => b.date.localeCompare(a.date))
          .map(
            (daily: DailyStatsDto): RegionDailyGameStatQueryResult => ({
              date: formatDateToString(daily.date, DateFormats.DayMonthYearDash),
              dayOfWeek: formatDateToString(daily.date, DateFormats.WeekdayName),
              gamesCreated: daily.count,
              applicationsCreated: appsCreatedMap.get(daily.date) ?? 0,
              applicationsAccepted: appsAcceptedMap.get(daily.date) ?? 0
            })
          );

        const totalGames: number = dailyStats.reduce(
          (sum: number, d: RegionDailyGameStatQueryResult) => sum + d.gamesCreated,
          0
        );
        const totalApplications: number = dailyStats.reduce(
          (sum: number, d: RegionDailyGameStatQueryResult) => sum + d.applicationsCreated,
          0
        );
        const totalAccepted: number = dailyStats.reduce(
          (sum: number, d: RegionDailyGameStatQueryResult) => sum + d.applicationsAccepted,
          0
        );

        return { regionName: region.regionName, totalGames, totalApplications, totalAccepted, dailyStats };
      })
      .filter(
        (region: RegionGamesGroupQueryResult) =>
          region.totalGames > 0 || region.totalApplications > 0 || region.totalAccepted > 0
      )
      .sort((a: RegionGamesGroupQueryResult, b: RegionGamesGroupQueryResult) => b.totalGames - a.totalGames);
  }

  private toDateRegionMap(regions: RegionDailyGamesStatsDto[]): Map<string, Map<string, number>> {
    const result: Map<string, Map<string, number>> = new Map();
    for (const region of regions) {
      for (const daily of region.dailyStats) {
        if (daily.count > 0) {
          const dateMap: Map<string, number> = result.get(daily.date) ?? new Map();
          dateMap.set(region.regionName, daily.count);
          result.set(daily.date, dateMap);
        }
      }
    }
    return result;
  }

  private buildDayRegions(
    date: string,
    gamesMap: Map<string, Map<string, number>>,
    appsCreatedMap: Map<string, Map<string, number>>,
    appsAcceptedMap: Map<string, Map<string, number>>
  ): DayRegionBreakdownQueryResult[] {
    const allRegions: Set<string> = new Set([
      ...(gamesMap.get(date)?.keys() ?? []),
      ...(appsCreatedMap.get(date)?.keys() ?? []),
      ...(appsAcceptedMap.get(date)?.keys() ?? [])
    ]);

    return [...allRegions]
      .map(
        (regionName: string): DayRegionBreakdownQueryResult => ({
          regionName,
          gamesCreated: gamesMap.get(date)?.get(regionName) ?? 0,
          applicationsCreated: appsCreatedMap.get(date)?.get(regionName) ?? 0,
          applicationsAccepted: appsAcceptedMap.get(date)?.get(regionName) ?? 0
        })
      )
      .sort((a: DayRegionBreakdownQueryResult, b: DayRegionBreakdownQueryResult) => b.gamesCreated - a.gamesCreated);
  }

  private buildCategoryRegions(
    date: string,
    regionsByDate: Map<string, Map<string, number>>
  ): CategoryRegionBreakdownQueryResult[] {
    const dateMap: Map<string, number> = regionsByDate.get(date) ?? new Map();
    return [...dateMap.entries()]
      .map(
        ([regionName, count]: [string, number]): CategoryRegionBreakdownQueryResult => ({
          regionName,
          count
        })
      )
      .sort((a: CategoryRegionBreakdownQueryResult, b: CategoryRegionBreakdownQueryResult) => b.count - a.count);
  }

  private toRegionMap(regions: RegionDailyGamesStatsDto[]): Map<string, Map<string, number>> {
    const result: Map<string, Map<string, number>> = new Map();
    for (const region of regions) {
      result.set(region.regionName, this.toMap(region.dailyStats));
    }
    return result;
  }

  private toMap(stats: DailyStatsDto[]): Map<string, number> {
    return new Map(stats.map((dto: DailyStatsDto) => [dto.date, dto.count]));
  }
}
